import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { shareObservable } from '@app/helpers/rxjs-helper';
import { FacebookPagesService } from '@app/modules/facebook-pages/facebook-pages.service';
import { FacebookPageModel } from '@app/modules/facebook-pages/models/facebook-page.model';
import { Brand } from '@app/shared/models/brand';
import { FilterOperator, FilterRequest } from '@app/shared/models/filter';
import {
  GeoKLocation,
  SelectLocationTableData,
} from '@app/shared/models/geo-k-location';
import {
  LocationVerificationStatus,
  ModalReturnType,
  SelectLocationDialogComponentData,
} from '@app/shared/models/locations-filters';
import {
  PageEventData,
  Pagination,
  PagingRequest,
} from '@app/shared/models/paging.class';
import { Tag } from '@app/shared/models/tag';
import { CompanyService } from '@app/shared/services/company.service';
import { IntegrationsService } from '@app/shared/services/integrations.service';
import { GeoSnackBarService } from '@app/shared/services/snack-bar/snack-bar.service';
import { UserService } from '@app/shared/services/user.service';
import { AuthFacade } from '@app/shared/store/auth/auth.facade';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  finalize,
  Observable,
  retry,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-select-location-dialog',
  templateUrl: './select-location-dialog.component.html',
  styleUrls: ['./select-location-dialog.component.scss'],
})
export class SelectLocationDialogComponent implements OnInit, OnDestroy {
  isLoading = true;
  displayedColumns: string[] = ['select', 'location', 'verified'];
  dataSource = new MatTableDataSource<SelectLocationTableData>([]);
  isMultipleSelection = !this.data.singleSelection;
  selection = new SelectionModel<string>(this.isMultipleSelection, []);
  showSelectAllLocationBanner = false;
  pageSize = 10;
  paging: Pagination = {} as Pagination;
  lastPageRequest: PagingRequest = { page: 1, limit: this.pageSize };
  filter: FilterRequest[] = [];
  currentFilter: LocationVerificationStatus = LocationVerificationStatus.ALL;
  form!: FormGroup;
  companyId!: string;
  brands: Brand[] = [];
  tags: Tag[] = [];
  private locations: GeoKLocation[] = [];
  private allLocations: GeoKLocation[] = [];
  private selectedLocationsObjects: GeoKLocation[] = [];
  locationsSubscription$: Subscription = new Subscription();
  brandsSubscription$: Subscription = new Subscription();
  tagsSubscription$: Subscription = new Subscription();
  searchSubscription$: Subscription = new Subscription();
  fetchLocationsSubscription$: Subscription = new Subscription();
  allLocationsSubscription$: Subscription = new Subscription();
  socialMediaLocationsSubscription$: Subscription = new Subscription();
  facebookPagesItems: FacebookPageModel[] = [];
  socialMediaDataSource = new MatTableDataSource<FacebookPageModel>([]);
  facebookPages: FacebookPageModel[] = [];
  socialMediaDisplayedColumns: string[] = ['select', 'location'];
  private readonly debounceTimeMs = 500;
  selectedOriginalLocations: GeoKLocation[] = [];
  initialOriginalLocationsIds: GeoKLocation[] = [];
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectLocationDialogComponentData,
    public userService: UserService,
    private companyService: CompanyService,
    private snackBarService: GeoSnackBarService,
    public matDialogRef: MatDialogRef<SelectLocationDialogComponent>,
    private authFacade: AuthFacade,
    private fb: FormBuilder,
    private integrationsService: IntegrationsService,
    private facebookPageService: FacebookPagesService,
    private translateService: TranslateService
  ) {
    if (this.data.selectedLocations?.length) {
      this.selectedOriginalLocations = [...this.data.selectedLocations];
    }
    if (this.data.intialLocationsIds?.length) {
      this.initialOriginalLocationsIds = [...this.data.intialLocationsIds];
    }
  }

  private initFilterForm() {
    this.form = this.fb.group({
      search: null,
      channel: this.data?.channel ? this.data.channel : 'google',
      brandIds: this.data?.brandId ? [this.data.brandId] : [],
      tagIds: [],
    });
  }

  ngOnInit() {
    this.initFilterForm();

    // Use initialOriginalLocationsIds if provided, otherwise use selectedOriginalLocations
    const initialSelection =
      this.initialOriginalLocationsIds.length > 0
        ? this.initialOriginalLocationsIds
        : this.selectedOriginalLocations;

    if (initialSelection.length > 0) {
      const locationsIds = initialSelection.map((it) => it.id ?? it);
      if (this.isMultipleSelection) {
        this.selection.setSelection(...locationsIds);
      } else {
        // Single selection mode - only select the first location
        if (locationsIds.length > 0) {
          this.selection.select(locationsIds[0]);
        }
      }
      this.selectedLocationsObjects = [...initialSelection];
    }

    if (this.data.filter) {
      this.currentFilter = this.data.filter;
    } else {
      this.filter = [];
      const length = this.data?.brandId?.length ?? 0;
      if (length != 0) {
        this.filter?.push({
          operation: FilterOperator.IN,
          property: 'brandId',
          value: this.data?.brandId as string,
        });
      }
    }
    if (this.form.value.channel === 'google') {
      this.fetchPaginatedLocations();
    } else {
      this.fetchPaginatedFacebookLocations();
    }

    // Add custom filters if provided
    if (
      this.data.customFilters &&
      this.data.customFilters.length > 0 &&
      this.initialOriginalLocationsIds.length > 0
    ) {
      this.filter = [...this.filter, ...this.data.customFilters];
    }

    this.loadSearchFormData();
    this.listenForSearch();
  }

  listenForSearch() {
    this.searchSubscription$ = this.form.valueChanges
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        tap((value) => {
          this.filter = [];
          if (value['brandIds'] && !this.data.brandId) {
            const length = (value['brandIds'] as string[]).length;
            if (length != 0) {
              this.filter?.push({
                operation: FilterOperator.IN,
                property: 'brandId',
                value: value['brandIds'] as string[],
              });
            }
          }

          if (value['tagIds']) {
            const length = (value['tagIds'] as string[]).length;
            if (length != 0) {
              this.filter?.push({
                operation: FilterOperator.IN,
                property: 'tagId',
                value: value['tagIds'],
              });
            }
          }

          if (value['search']?.trim()) {
            this.filter?.push({
              operation: FilterOperator.SEARCH,
              property: '',
              value: value['search'].trim(),
            });
          }

          if (value['channel'] === 'google') {
            this.fetchPaginatedLocations(1, this.pageSize);
          } else {
            this.fetchPaginatedFacebookLocations();
          }
        })
      )
      .subscribe();
  }

  loadSearchFormData() {
    this.brandsSubscription$ = this.userService
      .getMyBrands()
      .pipe(tap((brands: Brand[]) => (this.brands = brands)))
      .subscribe();
    this.tagsSubscription$.add(this.loadTags().subscribe());
  }

  loadTags(): Observable<Tag[]> {
    return this.authFacade.selectUserCompanyId$.pipe(
      shareObservable(),
      switchMap((companyId) => {
        this.companyId = companyId;
        return this.companyService.getAllTags(companyId);
      }),
      tap((tags: Tag[]) => {
        this.tags = tags;
      })
    );
  }

  clearFilter() {
    this.form.reset();
  }

  private getFilter(input: LocationVerificationStatus): FilterRequest[] {
    let filterByVerification!: FilterRequest[];
    const brandsFilterRequest: FilterRequest[] = [];
    switch (input) {
      case 'all-good':
        filterByVerification = [
          {
            operation: FilterOperator.EQUAL,
            property: 'verificationStatus',
            value: 'VERIFIED',
          },
        ];
        break;
      case 'blocked':
        filterByVerification = [
          {
            operation: FilterOperator.IN,
            property: 'verificationStatus',
            value: [
              'DISABLED',
              'EXPIRED',
              'SUSPENDED',
              'CONFLICT',
              'REQUIRED',
              'DUPLICATE',
            ],
          },
        ];
        break;
    }
    let total: FilterRequest[] = [];

    if (this.filter) {
      total = total.concat(this.filter);
    }

    if (this.data.brandId) {
      brandsFilterRequest.push({
        operation: FilterOperator.IN,
        property: 'brandId',
        value: [this.data.brandId],
      });
      total = total.concat(brandsFilterRequest);
    }
    if (filterByVerification) {
      total = total.concat(filterByVerification);
    }
    return total;
  }

  isAllSelected() {
    return this.dataSource.data.every((data) =>
      this.selection.selected.includes(data.id)
    );
  }

  clearAllSelections() {
    this.selection.clear();
    this.selectedLocationsObjects = [];
    this.showSelectAllLocationBanner = false;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.clearAllSelections();
      return;
    }
    if (this.paging.totalCount >= this.pageSize) {
      this.showSelectAllLocationBanner = true;
    }
    const list = this.dataSource.data.map((x) => x.id);
    if (list) {
      this.selection.select(...list);
      this.selectedLocationsObjects = [...this.locations];
    }
  }

  submitForm(): void {
    if (this.data.inViewMode) {
      return;
    }

    const closeWithOriginalSelection = () => {
      if (this.data.returnType === ModalReturnType.GKLOCATION) {
        this.matDialogRef.close(
          this.data.withNetwork
            ? {
                network: this.form.value.channel,
                data: this.selectedLocationsObjects,
              }
            : this.selectedLocationsObjects
        );
        this.matDialogRef.close(this.initialOriginalLocationsIds);
      } else {
        this.matDialogRef.close(
          this.initialOriginalLocationsIds.map((location) => location.id)
        );
      }
    };

    if (this.isMultipleSelection) {
      if (this.selection.isEmpty()) {
        closeWithOriginalSelection();
        return;
      }

      if (this.data.returnType === ModalReturnType.GKLOCATION) {
        if (
          !this.selectedLocationsObjects ||
          this.selectedLocationsObjects.length === 0
        ) {
          closeWithOriginalSelection();
          return;
        }
        this.matDialogRef.close(this.selectedLocationsObjects);
      } else {
        this.matDialogRef.close(
          this.data.withNetwork
            ? {
                network: this.form.value.channel,
                data: this.selection.selected,
              }
            : this.selection.selected
        );
      }
    } else {
      const location = this.locations.find(
        (it: GeoKLocation) => it.id === this.selection.selected[0]
      );
      this.matDialogRef.close(
        this.data.withNetwork
          ? { network: this.form.value.channel, data: location }
          : location
      );
      const selectedId = this.selection.selected[0];

      if (!selectedId) {
        closeWithOriginalSelection();
        return;
      }

      // const location =
      //   this.locations.find((it: GeoKLocation) => it.id === selectedId) ||
      //   this.selectedOriginalLocations.find(
      //     (it: GeoKLocation) => it.id === selectedId
      //   );

      // if (location) {
      //   this.matDialogRef.close(location);
      // } else {
      //   closeWithOriginalSelection();
      // }
    }
  }

  fetchPaginatedLocations(page = 1, limit = this.pageSize) {
    this.isLoading = true;
    this.lastPageRequest = {
      page: page,
      limit: limit,
    } as PagingRequest;

    const shouldLimitToInitialLocations =
      this.data.limitToInitialLocations ?? true;

    if (shouldLimitToInitialLocations && this.initialOriginalLocationsIds.length > 0) {
      this.filter = [];

      this.filter?.push({
        operation: FilterOperator.IN,
        property: 'gbp_locations.id',
        value: this.initialOriginalLocationsIds.map(
          (location) => location.id
        ) as string[],
      });
    }
    this.fetchLocationsSubscription$ = this.userService
      .getMyLocations(
        this.lastPageRequest,
        this.getFilter(this.currentFilter),
        'summary'
      )
      .pipe(
        tap((it) => {
          this.paging = {
            totalCount: it.pagination.totalCount,
            pageIndex: it.pagination.pageIndex,
          };
          const selectedIds = new Set(this.selection.selected);
          const sortedItems = [...it.items].sort((a, b) => {
            const aSelected = selectedIds.has(a.id);
            const bSelected = selectedIds.has(b.id);

            if (aSelected === bSelected) {
              return 0;
            }

            return aSelected ? -1 : 1;
          });

          this.locations = sortedItems;
          this.dataSource.data = sortedItems.map((item) => {
            const address = (item as GeoKLocation & {
              address?: { addressLine1?: string; addressLine2?: string };
            }).address;

            const addressLine1 =
              (address?.addressLine1 as string | undefined) ||
              (item as Partial<GeoKLocation>).addressLine1 ||
              '';
            const addressLine2 =
              (address?.addressLine2 as string | undefined) ||
              (item as Partial<GeoKLocation>).addressLine2 ||
              '';

            return {
              ...item,
              addressLine1,
              addressLine2,
              address: `${addressLine1} ${addressLine2}`.trim(),
            };
          });
        }),
        catchError(() => {
          this.snackBarService.error({
            title: this.translateService.instant('GENERAL.ERROR'),
            message: this.translateService.instant('GENERAL.INTERNAL_ERROR'),
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  fetchPaginatedFacebookLocations(page = 1, limit = this.pageSize): void {
    this.isLoading = true;
    this.lastPageRequest = {
      page: page,
      limit: limit,
    } as PagingRequest;

    this.socialMediaLocationsSubscription$ = this.facebookPageService
      .getActivatedFacebookPages(
        this.form.value.channel,
        this.lastPageRequest,
        this.getFilter(this.currentFilter)
      )
      .pipe(
        tap(({ items, pagination }) => {
          this.paging = pagination;
          this.isLoading = false;
          this.facebookPagesItems = items;
          this.paging = {
            totalCount: pagination.totalCount,
            pageIndex: pagination.pageIndex,
          };
          this.socialMediaDataSource.data = this.facebookPagesItems?.map(
            (elm) => ({
              ...elm,
              fullStarRating: Math.floor(elm.average_rating || 0),
              address:
                elm.street || elm.city
                  ? [elm.street, elm.city].join(' - ')
                  : '',
            })
          );
        }),
        catchError(() => {
          this.isLoading = false;
          this.snackBarService.error({
            title: this.translateService.instant('GENERAL.ERROR'),
            message: this.translateService.instant('GENERAL.INTERNAL_ERROR'),
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  handleChangePage(pagingDetails: PageEventData) {
    this.fetchPaginatedLocations(
      pagingDetails.pageIndex,
      pagingDetails.pageSize
    );
  }

  ngOnDestroy() {
    this.locationsSubscription$.unsubscribe();
    this.fetchLocationsSubscription$.unsubscribe();
    this.brandsSubscription$.unsubscribe();
    this.tagsSubscription$.unsubscribe();
    this.searchSubscription$.unsubscribe();
    this.allLocationsSubscription$.unsubscribe();
    this.socialMediaLocationsSubscription$.unsubscribe();
  }

  private fetchAllLocations() {
    this.isLoading = true;
    this.allLocationsSubscription$ = this.userService
      .getMyLocations({}, this.getFilter(this.currentFilter), 'summary')
      .pipe(
        tap((data) => {
          this.allLocations = data.items;
          this.selection.select(...this.allLocations.map((x) => x.id));
          this.selectedLocationsObjects = this.allLocations;
          this.snackBarService.basic({
            title:
              `${this.allLocations.length}` +
              `  <div>${this.translateService.instant('GENERAL.LOCATIONS_FETCHED_AND_SELECTED')}</div>`,
          });
        }),
        retry(1),
        catchError(() => {
          this.snackBarService.error({
            title: this.translateService.instant('GENERAL.ERROR'),
            message: this.translateService.instant('GENERAL.INTERNAL_ERROR'),
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  selectAllLocations() {
    if (this.paging.totalCount >= this.pageSize) {
      this.fetchAllLocations();
    }
  }

  toggleSelection(id: string) {
    const isSelected = this.selection.isSelected(id);
    const location = this.locations.find((it) => it.id === id);
    this.selection.toggle(id);
    if (!isSelected) {
      if (location) {
        this.selectedLocationsObjects.push(location);
      }
    } else {
      this.selectedLocationsObjects = this.selectedLocationsObjects.filter(
        (item) => item.id != id
      );
    }
  }
}
